import { FC, lazy, useLayoutEffect } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { NetworkPointRedirect } from 'src/components/domain-specifics/network-points/network-point-redirect';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { AutoFullwidth } from 'src/components/layout/auto-fullwidth';
import { Stack } from 'src/components/layout/stack';
import { RoleGateway } from 'src/components/role-gateway';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useWithinStoryPage } from 'src/hooks/use-within-story-page';
import { AggregateEu } from 'src/pages/aggregate-eu';
import { Dashboard } from 'src/pages/dashboard';
import { Debug } from 'src/pages/debug';
import { DeveloperTools } from 'src/pages/developer-tools';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { DirectFcfs } from 'src/pages/direct-fcfs';
import { Gdm } from 'src/pages/gdm';
import { Login } from 'src/pages/login';
import { Logout } from 'src/pages/logout';
import { Operators } from 'src/pages/operators';
import { FinancialSecurityAmend } from 'src/pages/operators/assignments/details/financial-securities/amend';
import { FinancialSecurityRelease } from 'src/pages/operators/assignments/details/financial-securities/release';
import { UserInvitation } from 'src/pages/organisation/users/invitation';
import { Platform } from 'src/pages/platform';
import { Registration } from 'src/pages/registration';
import { ReportingAuctions } from 'src/pages/reporting/auctions';
import { ReportingAuction } from 'src/pages/reporting/auctions/details';
import { ReportingBalancingGroupAllocations } from 'src/pages/reporting/balancing-group-allocations';
import { BgaRedirect } from 'src/pages/reporting/balancing-group-allocations/balancing-group-route-redirect';
import { ReportingBooking } from 'src/pages/reporting/direct-fcfs/bookings';
import { FcfsBookings } from 'src/pages/reporting/fcfs-bookings';
import { MonthlyStatements } from 'src/pages/reporting/monthly-statements';
import { MonthlyStatementRedirect } from 'src/pages/reporting/monthly-statements/monthly-statement-redirect';
import { ReportingTransactions } from 'src/pages/reporting/my-transactions';
import { DealAllocationToBalancingGroups } from 'src/pages/reporting/my-transactions/deals/allocation-to-balancing-groups';
import { CreateConversion } from 'src/pages/reporting/my-transactions/deals/conversions/create';
import { ConversionDetail } from 'src/pages/reporting/my-transactions/deals/conversions/details';
import { MultiYearTransactionDetail } from 'src/pages/reporting/my-transactions/multi-year';
import { MultiYearAllocationToBalancingGroups } from 'src/pages/reporting/my-transactions/multi-year/allocation-to-balancing-groups';
import { TradeAllocationToBalancingGroups } from 'src/pages/reporting/my-transactions/trades/allocation-to-balancing-groups';
import { ReportingUploads } from 'src/pages/reporting/products';
import { ReportingPrimaryRemitReports } from 'src/pages/reporting/remit-reports';
import { ReportingSecondaryTradings } from 'src/pages/reporting/secondary-tradings/overview';
import { ReportingSecondaryTradeProposal } from 'src/pages/reporting/secondary-tradings/trade-proposal';
import { ShipperTransactions } from 'src/pages/reporting/shipper-transactions';
import { ReportingStandard } from 'src/pages/reporting/standard';
import { CreateSurrenderRequest } from 'src/pages/reporting/surrender/create';
import { SurrenderRequestDetail } from 'src/pages/reporting/surrender/detail';
import { SurrenderOverview } from 'src/pages/reporting/surrender/overview';
import { Transparency } from 'src/pages/reporting/transparency';
import { ProfileSettingsRedirect, Settings } from 'src/pages/settings';
import { ShipperUserDetails } from 'src/pages/shipper-admin/details';
import { EditShipperUserDetails } from 'src/pages/shipper-admin/details/edit';
import { ShippersView } from 'src/pages/shippers';
import { ShipperDetails } from 'src/pages/shippers/details';
import { EditShipperDocument } from 'src/pages/shippers/edit-document';
import { InvoiceDetail } from 'src/pages/shippers/invoices/detail';
import { UploadDocumentsToShippers } from 'src/pages/shippers/upload-documents';
import { SnamBidCollectorBiddingPage } from 'src/pages/snam-bid-collector/bidding';
import { SnamBiddingSuccessPage } from 'src/pages/snam-bid-collector/bidding/success';
import { SnamBidCollectorSettingsPage } from 'src/pages/snam-bid-collector/settings';
import { StorageLocationsOverview } from 'src/pages/storage/locations';
import { StorageOffers } from 'src/pages/storage/offers';
import { AuctionDetailPage } from 'src/pages/transport/auctions/details';
import { RunningAuctionsOverview } from 'src/pages/transport/auctions/overview';
import { FcfsBooking } from 'src/pages/transport/fcfs-booking';
import { NetworkPoint } from 'src/pages/transport/network-points/details';
import { NetworkPointsOverview } from 'src/pages/transport/network-points/overview';
import { NetworkPointsRedirect } from 'src/pages/transport/network-points/redirect';
import { TransportSecondaryTradingsTrade } from 'src/pages/transport/secondary-tradings/trade';
import { UrgentMarketMessages } from 'src/pages/umm';

const UnknownError = lazy(() => import('src/pages/errors/unknown'));

const MarketInformation = lazy(() => import('src/pages/market-information'));

const ReportingRedirect: FC = () => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  if (monolithUser?.isTso) {
    return <Redirect to="/reporting/auctions" />;
  } else {
    return <Redirect to="/reporting/standard" />;
  }
};

const RootRedirect: FC = () => {
  const { replace } = useHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => replace('/platform/#/start'), []);
  return null;
};

export const Routes: FC = () => {
  const developerTools = useDeveloperTools();
  const withinStoryPage = useWithinStoryPage();

  return (
    <Switch>
      <Route path="/platform" exact>
        {withinStoryPage ? (
          <p>
            This is an Angular page. This page is <strong>not</strong>{' '}
            documentend in Storybook.
          </p>
        ) : (
          <Platform />
        )}
      </Route>

      <Route path="/login">
        <Login />
      </Route>

      <Route path="/logout">
        <Logout />
      </Route>

      <Route path="/registration">
        <Registration />
      </Route>

      <Route path="/organisation/users/invitation/:invitationId">
        <UserInvitation />
      </Route>

      <Redirect
        from="/network-points/:oldNetworkPointId/*"
        to="/transport/network-points/:oldNetworkPointId/*"
      />

      <Route path="/umm">
        <UrgentMarketMessages />
      </Route>

      <Redirect
        from="/mail-redirect/surrender-service/surrender-request/:surrenderRequestId"
        to="/reporting/surrenders/:surrenderRequestId"
      />
      <Redirect
        from="/mail-redirect/capacity-management/shipper-missing-bga-for-deal/:dealId/:direction"
        to="/reporting/my-transactions/deals/:dealId/allocation-to-balancing-groups/:direction"
      />
      <Redirect
        from="/mail-redirect/capacity-management/shipper-missing-bga-for-trade/:tradeId/:direction"
        to="/reporting/my-transactions/trades/:tradeId/allocation-to-balancing-groups/:direction"
      />
      <Route path="/mail-redirect/capacity-management/bga-overview/:nwpUuid/:balancingGroupId/:balancingGroupName/:allocationIntervalIntersectionStart/:allocationIntervalIntersectionEnd">
        <BgaRedirect />
      </Route>
      <Redirect
        from="/mail-redirect/capacity-management/conversion/:conversionId"
        to="/reporting/my-transactions/conversions/:conversionId"
      />

      <Route path="/reporting">
        <AutoFullwidth />
        <Switch>
          <Route path="/reporting/tr">
            <Transparency />
          </Route>
          <Route path="/reporting/monthly-statements">
            <MonthlyStatements />
          </Route>
          <Route path="/reporting/balancing-group-allocations">
            <ReportingBalancingGroupAllocations />
          </Route>
          <Route path="/reporting/shipper-transactions/conversions/:conversionRequestId">
            <ConversionDetail />
          </Route>
          <Route path="/reporting/shipper-transactions/multi-year/details/:multiYearAllocationId">
            <MultiYearTransactionDetail />
          </Route>
          <Route path="/reporting/shipper-transactions">
            <ShipperTransactions />
          </Route>
          <Route path="/reporting/products">
            <ReportingUploads />
          </Route>
          <Route path="/reporting/standard">
            <ReportingStandard />
          </Route>
          <Route path="/reporting/surrenders" exact>
            <SurrenderOverview />
          </Route>
          <Route path="/reporting/auctions/details/:auctionId">
            <ReportingAuction />
          </Route>
          <Route
            path={[
              '/reporting/auctions',
              '/reporting/auctions/short-and-long-term-auctions',
              '/reporting/auctions/pending-auctions',
              '/reporting/auctions/reverse-auctions',
            ]}
            exact
          >
            <ReportingAuctions />
          </Route>
          <Route path="/reporting/secondary/proposals/:proposalId">
            <ReportingSecondaryTradeProposal />
          </Route>
          <Route path="/reporting/secondary/trades/:tradeId">
            <TransportSecondaryTradingsTrade />
          </Route>
          <Route path="/reporting/secondary">
            <ReportingSecondaryTradings />
          </Route>
          <Route path="/reporting/remit-reports">
            <ReportingPrimaryRemitReports />
          </Route>
          <Route path="/reporting/my-transactions/multi-year/details/:multiYearAllocationId">
            <MultiYearTransactionDetail />
          </Route>
          <Route path="/reporting/my-transactions/deals/:dealId/conversions/create">
            <CreateConversion />
          </Route>
          <Route path="/reporting/my-transactions/deals/:dealId/allocation-to-balancing-groups">
            <DealAllocationToBalancingGroups />
          </Route>
          <Route path="/reporting/my-transactions/trades/:id/allocation-to-balancing-groups">
            <TradeAllocationToBalancingGroups />
          </Route>
          <Route path="/reporting/my-transactions/multi-year/:multiYearAllocationId/allocation-to-balancing-groups">
            <MultiYearAllocationToBalancingGroups />
          </Route>
          <Route path="/reporting/my-transactions/conversions/:conversionRequestId">
            <ConversionDetail />
          </Route>
          <Route path="/reporting/my-transactions">
            <ReportingTransactions />
          </Route>
          <Route path="/reporting/surrenders/create/:transactionId">
            <CreateSurrenderRequest />
          </Route>
          <Route path="/reporting/surrenders/:surrenderRequestId">
            <SurrenderRequestDetail />
          </Route>
          <Route path="/reporting/fcfs" exact>
            <FcfsBookings />
          </Route>
          <Route path="/reporting/direct-fcfs/bookings/:bookingId">
            <ReportingBooking />
          </Route>
          <Route path="/reporting" exact>
            <ReportingRedirect />
          </Route>
          <Route>
            <ThrowNotFound />
          </Route>
        </Switch>
      </Route>

      <Route path="/snam-bid-collector">
        <SnamBidCollectorSettingsPage />
      </Route>
      <Route path="/snam-bidding" exact>
        <SnamBidCollectorBiddingPage />
      </Route>
      <Route path="/snam-bidding/success" exact>
        <SnamBiddingSuccessPage />
      </Route>

      <Redirect
        from="/settings/registration"
        to="/settings/assignment-registration"
      />

      <Route path="/settings">
        <Settings />
      </Route>
      <Route path="/profile-settings">
        <ProfileSettingsRedirect />
      </Route>
      <Redirect from="/profile" exact to="/profile-settings/profile-settings" />

      <Route path="/dashboard" exact>
        <Dashboard />
      </Route>

      <Route path="/shipper-admin/details/:oldUserId/edit">
        <EditShipperUserDetails />
      </Route>

      <Route path="/shipper-admin/details/:oldUserId">
        <ShipperUserDetails />
      </Route>

      <Route path="/mail-redirect/capacity-management/tso-new-invoice-created-for-monthly-statement/:monthlyStatementId">
        <MonthlyStatementRedirect />
      </Route>
      <Redirect
        from="/mail-redirect/capacity-management/tso-new-invoice-created/:invoiceId"
        to="/shippers/invoices/:invoiceId"
      />

      <Route path="/shippers/invoices/:invoiceId">
        <InvoiceDetail />
      </Route>

      <Redirect
        from="/shippers/details/:shipperRefId/admins"
        to="/shippers/:shipperRefId/admins"
      />
      <Redirect
        from="/shippers/details/:shipperRefId"
        to="/shippers/:shipperRefId"
      />

      <Route path="/shippers/documents/:operatorDocumentId">
        <RoleGateway allowed={['TSO_USER']}>
          <EditShipperDocument />
        </RoleGateway>
      </Route>

      <Route
        path={['/shippers/list', '/shippers/documents', '/shippers/invoices']}
      >
        <ShippersView />
      </Route>
      <Redirect exact from="/shippers" to="/shippers/list" />

      <Route path="/shippers/upload-documents">
        <RoleGateway allowed={['TSO_USER', 'SSO_ADMIN']}>
          <UploadDocumentsToShippers />
        </RoleGateway>
      </Route>

      <Route
        path={[
          '/shippers/:shipperRefId',
          '/mail/shippers/:shipperRefId',
          '/dashboard/shippers/:shipperUuid',
        ]}
      >
        <RoleGateway allowed={['TSO_USER', 'SSO_ADMIN']}>
          <ShipperDetails />
        </RoleGateway>
      </Route>

      <Route path="/shipper">
        <RoleGateway allowed={['TSO_USER']}>
          <ShippersView />
        </RoleGateway>
      </Route>

      <Route path="/storage/locations" exact>
        <StorageLocationsOverview />
      </Route>

      <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/cash-deposit/:financialSecurityId/release">
        <FinancialSecurityRelease type="CASH_DEPOSIT" />
      </Route>
      <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/cash-deposit/:financialSecurityId/amend">
        <FinancialSecurityAmend type="CASH_DEPOSIT" />
      </Route>
      <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/rating/:financialSecurityId/amend">
        <FinancialSecurityAmend type="RATING" />
      </Route>
      <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/bank-guarantee/:financialSecurityId/amend">
        <FinancialSecurityAmend type="BANK_GUARANTEE" />
      </Route>
      <Route path="/dashboard/operators/assignments/:operatorUuid/financial-securities/parent-company-guarantee/:financialSecurityId/amend">
        <FinancialSecurityAmend type="PARENT_COMPANY_GUARANTEE" />
      </Route>

      <Route path={['/operators', '/dashboard/operators']}>
        <Operators />
      </Route>

      <Redirect
        from="/mail-redirect/capacity-management/shipper-new-invoice-document-available/:invoiceId"
        to="/operators/invoices/:invoiceId"
      />
      <Redirect
        from="/mail-redirect/capacity-management/shipper-invoice-cancelled/:invoiceId"
        to="/operators/invoices/:invoiceId"
      />
      <Redirect
        from="/mail-redirect/capacity-management/shipper-new-invoice-available/:invoiceId"
        to="/operators/invoices/:invoiceId"
      />
      <Redirect from="/assignments" to="/operators/assignments" />
      <Redirect from="/operator-documents" to="/operators/operator-documents" />

      <Route path="/transport/secondary/trades/:tradeId">
        <TransportSecondaryTradingsTrade />
      </Route>

      <Route path="/transport/secondary/proposals/:proposalId">
        <ReportingSecondaryTradeProposal />
      </Route>

      <Route path="/transport/network-points" exact>
        <NetworkPointsOverview />
      </Route>

      <Route path="/transport/network-points/redirect/:networkPointId" exact>
        <NetworkPointsRedirect />
      </Route>

      <Route path="/transport/network-points/:oldNetworkPointId">
        <NetworkPoint />
      </Route>

      <Route path="/network-points-redirect/:networkPointUuid">
        <NetworkPointRedirect />
      </Route>

      <Route path="/transport/auctions/list" exact>
        <RunningAuctionsOverview />
      </Route>

      <Route
        path={[
          '/transport/auctions/:auctionId',
          '/transport/auctions/:auctionId/:orderId',
        ]}
        exact
      >
        <AuctionDetailPage />
      </Route>

      <Route path="/transport/fcfs-booking">
        <FcfsBooking />
      </Route>

      <Route path="/storage/offers" exact>
        <StorageOffers />
      </Route>

      <Route path="/direct-fcfs">
        <DirectFcfs />
      </Route>

      <Route path="/market-information">
        <MarketInformation />
      </Route>

      <Route path="/gdm">
        <Gdm />
      </Route>

      <Route path="/aggregate-eu">
        <AggregateEu />
      </Route>

      <Route path="/error">
        <UnknownError />
      </Route>

      {developerTools && (
        <Route path="/developer-tools">
          <DeveloperTools />
        </Route>
      )}

      {PRISMA_CONFIG.stage === 'storybook' && (
        <Route path="/debug">
          <Debug />
        </Route>
      )}

      <Route path="/" exact>
        {PRISMA_CONFIG.stage === 'local' &&
        !PRISMA_CONFIG.oldFrontendUrl.includes('localhost:4200') ? (
          <Stack gap={1}>
            <p>Hello Developer!</p>

            <p>
              This is the root page of the React frontend. Normally we redirect
              the user to the Angular start page at this point, but it looks
              like you're running only the React frontend locally, so we keep
              you on this page.
            </p>
            <p>
              If you need the Angular frontend as well you can run{' '}
              <code>$ pnpm start:with-angular</code> instead of{' '}
              <code>$ pnpm start</code>.
            </p>

            <p>Happy coding!</p>
          </Stack>
        ) : (
          <RootRedirect />
        )}
      </Route>

      <Route>
        <ThrowNotFound />
      </Route>
    </Switch>
  );
};
